// src/utils/competencias.js
// Mapa de competências avaliadas pela IA em cada reunião
// (campo `competencias` da analise_operador_ia).
//
// Chaves compartilhadas entre os tipos de reunião (servico, comercial, cs)
// se referem à mesma habilidade — assim o radar consolida o operador que
// atua em mais de um papel.

export const COMPETENCIAS_TODAS = [
  // núcleo compartilhado
  { key: 'diagnostico',      label: 'Diagnóstico e Descoberta' },
  { key: 'proatividade',     label: 'Proatividade' },
  { key: 'dominio_tecnico',  label: 'Domínio Técnico' },
  { key: 'traducao_valor',   label: 'Tradução de Valor' },
  { key: 'conducao',         label: 'Condução' },
  { key: 'objecoes',         label: 'Gestão de Objeções' },
  { key: 'compromisso',      label: 'Compromisso' },
  { key: 'orientacao_dados', label: 'Orientação a Dados' },
  // específicas de vendas (comercial)
  { key: 'qualificacao',    label: 'Qualificação' },
  { key: 'fechamento',      label: 'Fechamento' },
  { key: 'apresentacao',    label: 'Apresentação do Produto' },
  // específicas de CS/retenção
  { key: 'empatia',         label: 'Empatia e Escuta' },
  // gestão de pessoas / 1:1 — papel GESTOR
  { key: 'escuta_ativa',      label: 'Escuta Ativa' },
  { key: 'perguntas_abertas', label: 'Perguntas Poderosas' },
  { key: 'feedback',          label: 'Feedback de Qualidade' },
  { key: 'desenvolvimento',   label: 'Desenvolvimento e Carreira' },
  { key: 'remocao_bloqueios', label: 'Remoção de Bloqueios' },
  { key: 'seguranca',         label: 'Segurança Psicológica' },
  { key: 'reconhecimento',    label: 'Reconhecimento' },
  { key: 'acompanhamento',    label: 'Follow-through da Gestão' },
  // gestão de pessoas / 1:1 — papel LIDERADO
  { key: 'participacao',      label: 'Participação Ativa' },
  { key: 'transparencia',     label: 'Transparência' },
  { key: 'protagonismo',      label: 'Protagonismo' },
  { key: 'receptividade',     label: 'Receptividade a Feedback' },
  { key: 'clareza_metas',     label: 'Clareza de Prioridades' },
  // reunião Vision (assessoria contábil com BI) — 'apresentacao' e 'compromisso' são compartilhadas
  { key: 'leitura_executiva',      label: 'Visão Executiva' },
  { key: 'destaque_positivo',      label: 'Destaque Positivo' },
  { key: 'gargalo',                label: 'Principal Gargalo' },
  { key: 'perguntas_estrategicas', label: 'Perguntas Estratégicas' },
  { key: 'impacto_potencial',      label: 'Impacto Potencial' },
  { key: 'recomendacao',           label: 'Recomendação da Assessoria' },
  // reunião Reforma Tributária — 'diagnostico', 'dominio_tecnico' e 'compromisso' são compartilhadas
  { key: 'explicacao_reforma',     label: 'Explicação da Reforma' },
  { key: 'cronograma_transicao',   label: 'Cronograma da Transição' },
  { key: 'impacto_cliente',        label: 'Impacto no Negócio' },
  { key: 'decisoes_estrategicas',  label: 'Decisões Estratégicas' },
  { key: 'preparacao_operacional', label: 'Preparação Operacional' },
];

// Conjunto do tipo serviço/consultivo (mantido para referência)
export const COMPETENCIAS_CONSULTIVO = COMPETENCIAS_TODAS.filter(c =>
  ['diagnostico', 'proatividade', 'dominio_tecnico', 'traducao_valor', 'conducao', 'objecoes', 'compromisso', 'orientacao_dados'].includes(c.key)
);

export const LABEL_COMPETENCIA = COMPETENCIAS_TODAS.reduce(
  (acc, c) => ({ ...acc, [c.key]: c.label }),
  {}
);

// Conjunto de competências avaliado em cada tipo de análise
// (espelha as rubricas dos prompts na tabela prompt_ia)
export const COMPETENCIAS_POR_TIPO = {
  servico:   ['diagnostico', 'proatividade', 'dominio_tecnico', 'traducao_valor', 'conducao', 'objecoes', 'compromisso', 'orientacao_dados'],
  comercial: ['diagnostico', 'qualificacao', 'dominio_tecnico', 'traducao_valor', 'apresentacao', 'conducao', 'objecoes', 'fechamento'],
  cs:        ['diagnostico', 'empatia', 'proatividade', 'traducao_valor', 'objecoes', 'conducao', 'compromisso'],
  gestao:    ['escuta_ativa', 'perguntas_abertas', 'feedback', 'desenvolvimento', 'remocao_bloqueios', 'seguranca', 'reconhecimento', 'acompanhamento',
              'participacao', 'transparencia', 'protagonismo', 'receptividade', 'clareza_metas'],
  vision:    ['leitura_executiva', 'destaque_positivo', 'gargalo', 'perguntas_estrategicas', 'impacto_potencial',
              'recomendacao', 'apresentacao', 'compromisso'],
  reforma_tributaria: ['diagnostico', 'explicacao_reforma', 'cronograma_transicao', 'impacto_cliente',
              'decisoes_estrategicas', 'preparacao_operacional', 'dominio_tecnico', 'compromisso'],
};

// Identidade de cada setor/tipo de análise (auth_user.tipo_analise)
export const TIPO_ANALISE_INFO = {
  servico: {
    label: 'Serviço / Consultivo',
    cor: '#d610b5',
    icone: 'fa-handshake',
    subtitulo: 'Análise inteligente da sua atuação consultiva com os clientes',
  },
  comercial: {
    label: 'Comercial / Vendas',
    cor: '#0d6efd',
    icone: 'fa-funnel-dollar',
    subtitulo: 'Análise inteligente da sua performance comercial',
  },
  cs: {
    label: 'CS / Retenção',
    cor: '#20c997',
    icone: 'fa-heart',
    subtitulo: 'Análise inteligente da sua atuação em retenção de clientes',
  },
  gestao: {
    label: 'Gestão de Pessoas / 1:1',
    cor: '#6610f2',
    icone: 'fa-people-arrows',
    subtitulo: 'Análise inteligente das suas reuniões one-on-one',
  },
  vision: {
    label: 'Reunião Vision (BI contábil)',
    cor: '#00205b',
    icone: 'fa-chart-pie',
    subtitulo: 'Análise inteligente das suas reuniões de assessoria com o Vision',
  },
  reforma_tributaria: {
    label: 'Reunião Reforma Tributária',
    cor: '#b8860b',
    icone: 'fa-scale-balanced',
    subtitulo: 'Análise inteligente das suas reuniões de orientação sobre a reforma tributária',
  },
};

/**
 * Rótulo de exibição para o tipo de reunião gravado no banco.
 * 'gestao' aparece como "Gestão/1:1" em todas as telas.
 */
export const labelTipoReuniao = (t) => {
  const x = (t || '').toLowerCase();
  if (x.includes('gest')) return 'Gestão/1:1';
  if (x.includes('vision')) return 'Vision';
  if (x.includes('reforma') || x.includes('tribut')) return 'Reforma Tributária';
  if (x.includes('comercial')) return 'Comercial';
  if (x === 'cs' || x.includes('reten')) return 'CS';
  if (x.includes('servico') || x.includes('serviço')) return 'Serviço';
  return t || '-';
};

/**
 * Monta os dados do radar a partir das avaliações da IA:
 * média da nota por competência entre as análises (ignora "nao_se_aplica").
 * Só entram no radar as competências que têm dados — um operador só de
 * vendas mostra as de vendas; um misto mostra a união.
 * Retorna null quando nenhuma análise possui o campo competencias
 * (dados antigos) — o chamador usa o fallback por palavras-chave.
 */
export const radarPorCompetenciasIA = (analises, tipo = null) => {
  const chavesDoTipo = tipo && COMPETENCIAS_POR_TIPO[tipo] ? COMPETENCIAS_POR_TIPO[tipo] : null;
  const acc = {};
  let temDados = false;

  (analises || []).forEach(a => {
    (a.competencias || []).forEach(c => {
      if (!c || !c.competencia) return;
      if (c.status === 'nao_se_aplica' || c.nota === null || c.nota === undefined) return;
      const nota = parseFloat(c.nota);
      if (isNaN(nota)) return;
      temDados = true;
      if (!acc[c.competencia]) acc[c.competencia] = [];
      acc[c.competencia].push(nota);
    });
  });

  if (!temDados) return null;

  return COMPETENCIAS_TODAS
    .filter(comp => !chavesDoTipo || chavesDoTipo.includes(comp.key))
    .map(comp => {
      const notas = acc[comp.key] || [];
      return {
        competencia: comp.label,
        score: notas.length
          ? Math.round((notas.reduce((s, n) => s + n, 0) / notas.length) * 10) / 10
          : null,
      };
    })
    .filter(d => d.score !== null);
};
